/**
 * WaveReportsPage — single-page Executive Memo for a wave.
 *
 * Lives at `/waves/:id/reports`. No tabs, no graphs. The memo reads
 * exactly like the Monday-morning email a brand rep sends to their CMO:
 * spend → revenue → ROAS, what worked, what didn't, what to do next.
 *
 * Three top-right actions:
 *   • Refresh         re-fetch fresh memo data
 *   • Save snapshot   freeze the current memo for week-over-week comparison
 *   • Download PDF    fpdf2-rendered text-heavy memo PDF
 *
 * Left rail (when snapshots exist): list of saved snapshots; click to
 * load a frozen memo into the page.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Loader2 } from 'lucide-react';
import DashboardLayout from '../layouts/DashboardLayout';
import ExecutiveMemo      from '../components/reports/ExecutiveMemo';
import ReportToolbar      from '../components/reports/ReportToolbar';
import SavedSnapshotsList from '../components/reports/SavedSnapshotsList';

const API = process.env.REACT_APP_BACKEND_URL;

export default function WaveReportsPage() {
  const { id: waveId } = useParams();

  const [memo, setMemo]             = useState(null);
  const [memoLoading, setMemoLoading] = useState(true);
  const [memoError, setMemoError]   = useState(null);
  const [snapshotLabel, setSnapshotLabel] = useState(null);    // non-null = viewing a frozen snapshot
  const [snapshots, setSnapshots]   = useState([]);
  const [snapsLoading, setSnapsLoading] = useState(true);

  // Wave name comes from the wave-pulse endpoint (already public to brand)
  const [waveName, setWaveName] = useState(null);

  // ── Fetchers ───────────────────────────────────────────────────────
  const loadMemo = useCallback(async () => {
    if (!waveId) return;
    setMemoLoading(true);
    setMemoError(null);
    setSnapshotLabel(null);
    try {
      const { data } = await axios.get(
        `${API}/api/brand/waves/${waveId}/reports/memo`,
        { withCredentials: true },
      );
      setMemo(data);
    } catch (e) {
      console.error('Memo load failed', e);
      setMemoError(e?.response?.data?.detail || e?.message || 'Failed to load memo.');
    } finally {
      setMemoLoading(false);
    }
  }, [waveId]);

  const loadSnapshots = useCallback(async () => {
    if (!waveId) return;
    setSnapsLoading(true);
    try {
      const { data } = await axios.get(
        `${API}/api/brand/waves/${waveId}/reports/snapshots`,
        { withCredentials: true },
      );
      setSnapshots(data.snapshots || []);
    } catch (e) {
      console.error('Snapshots load failed', e);
    } finally {
      setSnapsLoading(false);
    }
  }, [waveId]);

  useEffect(() => { loadMemo(); }, [loadMemo]);
  useEffect(() => { loadSnapshots(); }, [loadSnapshots]);

  // Wave header label
  useEffect(() => {
    if (!waveId) return;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/brand/waves/${waveId}/wave-pulse`,
          { withCredentials: true });
        setWaveName(data?.wave_name || null);
      } catch { /* ignore — page works without */ }
    })();
  }, [waveId]);

  const handleLoadSnapshot = async (snap) => {
    try {
      const { data } = await axios.get(
        `${API}/api/brand/waves/${waveId}/reports/snapshots/${snap.id}`,
        { withCredentials: true },
      );
      setMemo({ wave_id: waveId, enabled: true, data: data.data });
      setSnapshotLabel(data.label || snap.label);
    } catch (e) {
      console.error('Snapshot load failed', e);
    }
  };

  return (
    <DashboardLayout>
      <div className="px-6 py-6 max-w-[1100px] mx-auto" data-testid="wave-reports-page">
        {/* Top bar */}
        <div className="flex items-start justify-between gap-4 mb-7">
          <div className="min-w-0">
            <Link
              to={`/waves/${waveId}/control`}
              data-testid="reports-back-link"
              className="text-[10px] uppercase tracking-[0.18em] font-black text-gray-500 hover:text-white inline-flex items-center gap-1.5"
            >
              <ArrowLeft className="h-3 w-3" />
              Back to Wave Control Room
            </Link>
            <h1 className="text-[28px] font-black tracking-[-0.02em] text-white mt-1.5 leading-tight">
              Executive Memo
              {(memo?.data?.context?.campaign_name || waveName) && (
                <span className="text-gray-500 font-bold"> · {memo?.data?.context?.campaign_name || waveName}</span>
              )}
            </h1>
            <p className="text-[12px] text-gray-500 mt-1">
              One page. The answer to "did our money work?" — formatted to email straight to your CMO.
            </p>
          </div>
          <ReportToolbar
            waveId={waveId}
            reportId="memo"
            currentData={memo?.data}
            onRefresh={loadMemo}
            onSnapshotSaved={loadSnapshots}
          />
        </div>

        {/* 2-col: memo body | saved snapshots */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_240px] gap-6">
          <div
            className="rounded-2xl p-6 sm:p-8"
            style={{ background: '#181A20', border: '1px solid #2E3240', minHeight: 420 }}
            data-testid="reports-body-memo"
          >
            {memoLoading && !memo ? (
              <div className="flex items-center gap-3 py-10">
                <Loader2 className="h-5 w-5 animate-spin" style={{ color: '#A78BFA' }} />
                <span className="text-[13px] text-gray-400">Drafting memo…</span>
              </div>
            ) : memoError ? (
              <p className="text-[12.5px] text-red-400 py-6">Memo failed to load: {memoError}</p>
            ) : (
              <ExecutiveMemo
                data={memo?.data}
                isSnapshot={!!snapshotLabel}
                snapshotLabel={snapshotLabel}
              />
            )}
          </div>
          <SavedSnapshotsList
            snapshots={snapshots}
            activeReportId="memo"
            onLoad={handleLoadSnapshot}
            isLoading={snapsLoading}
          />
        </div>
      </div>
    </DashboardLayout>
  );
}
